// All text shown by the Ledger Loader screen lives here so it can be read and
// changed in one place. Friendly names fall back to the raw server name when a
// flow or file slot is not listed.

export const FLOWNAMES = {
  bl_counterblocked: "Bought Ledger: item headers and payment details together",
  bl_counterblocked_nl:
    "Bought Ledger: item headers, payment details and both Nominal Ledger sheets",
  bl_standard: "Bought Ledger: item headers only",
  bl_payment_standalone: "Bought Ledger: payment details only",
  sl_headers_lines: "Sales Ledger: item headers and item lines together",
  bl_dummy_payments: "Bought Ledger: dummy payments (two account sheets, numbered on load)",
};

export const SLOTNAMES = {
  bl_item_headers: "BL Item Headers",
  bl_payment_details: "BL Payment Details",
  nl_entries_1200: "NL Entries 1200",
  nl_entries_2100: "NL Entries 2100",
  sl_item_headers: "SL Item Headers",
  sl_item_lines: "SL Item Lines",
  bl_dummy_headers_1: "Dummy payments: first account sheet",
  bl_dummy_headers_2: "Dummy payments: second account sheet",
};

// Shown under a file slot instead of the generic counter hint. Slots not listed
// here show the counter hint when the flow reserves a counter.
export const SLOTHINTS = {
  nl_entries_1200:
    "Every row must post to account 1200. NL Entry carries the same BL Entry numbers as the item headers sheet. Nothing in this sheet is assigned by the server.",
  nl_entries_2100:
    "Every row must post to account 2100. NL Entry carries the same BL Entry numbers as the item headers sheet. Nothing in this sheet is assigned by the server.",
  sl_item_headers:
    "Usually the tab called SL Item headers. SL Entry and SL Tran Ref are kept exactly as keyed and must not already be in the ledger. The id column is removed.",
  sl_item_lines:
    "Usually the tab called L Item lines. Every line needs a header with the same entry number, account and invoice reference. id, sl_id and the created and updated columns are removed.",
  bl_dummy_headers_1:
    "One account's item header rows, for example SVPCONFU or GBTTREAS. Leave BL Entry and BL Tran Ref empty or leave the columns out: the loader numbers every line when you load. BL Counter is assigned by the database.",
  bl_dummy_headers_2:
    "One account's item header rows, for example SVPCONFU or GBTTREAS. Leave BL Entry and BL Tran Ref empty or leave the columns out: the loader numbers every line when you load. BL Counter is assigned by the database.",
};

export const FLOWHINTS = {
  bl_counterblocked:
    "Two sheets: BL Item Headers and BL Payment Details, with matching rows. The counter is assigned by the server when you load: BL Counter in the item headers sheet and BPD BL Counter in the payment details sheet. Leave both empty or leave them out altogether.",
  bl_counterblocked_nl:
    "Four sheets: BL Item Headers, BL Payment Details, NL Entries 1200 and NL Entries 2100. BL Counter in the item headers sheet and BPD BL Counter in the payment details sheet are assigned by the server, so leave both empty or leave them out. BL Entry numbers are kept exactly as you keyed them and checked against the ledger so none is already in use. The NL Entry numbers in both Nominal Ledger sheets must match the BL Entry numbers in the item headers sheet, and the NL Amount totals of the 1200 and 2100 sheets must balance to zero.",
  bl_standard:
    "One sheet: BL Item Headers. BL Counter is assigned by the database. Leave it empty or leave it out. BL Entry is not loaded by this flow and will be empty; for dummy payments that need BL Entry and AG references use the dummy payments flow.",
  bl_payment_standalone:
    "One sheet: BL Payment Details. BPD BL Counter must already be filled in, because each payment line points at an existing header.",
  sl_headers_lines:
    "Two sheets: SL Item Headers and SL Item Lines. Nothing is assigned by the server: SL Entry and the IM invoice numbers are kept exactly as you keyed them and checked so none is already in use. Each line is matched to its header by SL Entry; the invoice reference, account and total must agree, and every header needs at least one line. The entry and invoice counters are moved past the batch when you load.",
  bl_dummy_payments:
    "Two sheets from the same workbook, one per account. When you load, the loader takes one block of numbers from the ledger's last-used counters and gives every line its own BL Entry and its own AG reference (BL Tran Ref), first sheet first, then the second sheet. No DBeaver step and nothing to type in. The two sheets normally mirror each other: different BL A/C and opposite amounts, and the loader tells you if they do not net to zero.",
};

export const COPY = {
  page_title: "Ledger Loader",
  page_intro:
    "Load a spreadsheet into the Bought, Sales or Nominal Ledger. The file is checked first and nothing is written until you press Load.",
  flow_label: "What are you loading?",
  flow_required: "Required",
  flow_optional: "Optional",
  flow_cap: "Files up to {kb} kB can be loaded in one go.",
  flow_reservation: "BL Counter is assigned by the server when you load.",
  dateformat_label: "Dates in the sheet are written as",
  dateformat_hint:
    "Pick the way dates appear in your spreadsheet. Every date in the file must use this layout.",
  slot_mode_xlsx: "Excel file",
  slot_mode_csv: "CSV",
  slot_file_label: "Choose the Excel file",
  slot_reuse_label: "Or use a file you have already chosen",
  slot_sheet_label: "Which tab?",
  slot_csv_file_label: "Choose a CSV file",
  slot_csv_paste_label: "Or paste the CSV text here",
  slot_counter_hint:
    "The counter column is assigned by the server. Leave it empty in your sheet or leave the column out. If it has numbers in it the file will be refused.",
  slot_summary_xlsx: '{rows} rows and {cols} columns from tab "{sheet}" in {file}',
  slot_summary_csv: "About {rows} rows and {cols} columns from {file}",
  slot_columns_label: "Columns found",
  controls_title: "Optional checks",
  controls_hint:
    "If you know what the file should add up to, enter it here. The load is refused if the figures do not match.",
  controls_rows_label: "Number of rows expected",
  controls_total_label: "Total of column",
  controls_amount_label: "should be",
  controls_amount_hint: "Pounds and pence, for example 22072.60 or -5.00",
  payload_meter: "File size {kb} kB of {cap} kB",
  preview_button: "Check the file",
  preview_busy: "Checking...",
  commit_button: "Load into the ledger",
  commit_busy: "Loading...",
  back_button: "Back to the files",
  again_button: "Load another file",
  viewbatch_button: "View in Batches",
  retry_button: "Try the load again",
  preview_ok:
    "Checks passed. Nothing has been written yet. Review the rows below, then press Load into the ledger.",
  preview_failed:
    "Checks failed. Nothing has been written. Fix the file using the list below and check it again.",
  preview_duplicate:
    "This exact file set was already loaded as batch {batchid} by {who} on {when}. There is nothing more to do.",
  range_banner:
    "{counter} will be assigned {start} to {end} when you load. This is an estimate until the load completes.",
  assign_banner_preview:
    "{count} lines will be numbered when you load: BL Entry from about {estart} to {eend} and BL Tran Ref from about {rstart} to {rend}. These are today's next numbers; the final block is fixed when the load runs and may be higher.",
  assign_banner_commit:
    "BL Entry {estart} to {eend} and BL Tran Ref {rstart} to {rend} assigned to {count} lines, first sheet first, then the second.",
  assign_confirm:
    "BL Entry from about {estart} and BL Tran Ref from about {rstart} for {count} lines (estimate). ",
  batches_assigned: "BL Entry {estart} to {eend}, {rstart} to {rend}",
  file_summary:
    "{rows} rows, {kept} columns kept, {stripped} columns ignored, {trailing} empty rows at the end removed, {subs} characters replaced",
  file_stripped_hint: "These columns are managed by the system and were ignored: {list}",
  file_truncated: "Showing the first {shown} of {total} rows. All rows were checked.",
  issues_title: "Problems and notes",
  issues_summary_hint: "Full counts for every problem found. Click a line to see examples.",
  issues_detail_hint:
    "Showing the first 50 examples of each problem. The counts above are complete.",
  issues_none: "No problems found.",
  severity_ERROR: "Must fix",
  severity_WARNING: "Check",
  severity_INFO: "Note",
  sldate_title: "SL Date is not today",
  sldate_row: "{rows} rows have an SL Date of {date}, not today.",
  sldate_row_one: "1 row has an SL Date of {date}, not today.",
  sldate_advice:
    "If the workbook uses TODAY(), open it in Excel, save it and choose the file again. If the date is meant to be in the past, you can still load.",
  stop_warning:
    "The ledger table is missing a safety check. The load cannot go ahead. Contact support.",
  commit_confirm_title: "Load into the live ledger?",
  commit_confirm_body:
    "{flow}. {filelist}. {range}This writes to the live ledger and cannot be undone from this screen.",
  commit_ok: "Loaded. Batch {batchid}: {rows} rows written.",
  commit_range:
    "{counter} assigned {start} to {end}. The counter has been moved on to {next}.",
  commit_soft:
    "This file set had already been loaded as batch {batchid}. Nothing was written again.",
  commit_soft_nobatch: "This file set had already been loaded. Nothing was written again. The Batches tab lists it.",
  batches_title: "Batches",
  batches_hint: "Every load made through this screen, newest first.",
  batches_flow: "Flow",
  batches_all: "All",
  batches_by: "Loaded by (exact email)",
  batches_fp: "Showing one batch by its fingerprint",
  batches_older: "Load older",
  batches_empty: "No batches yet.",

  // Errors, local and transport
  flows_failed:
    "The loader could not start. The server said: {text}. If this says permission denied, ask support to add the Ledger Loader permission to your user.",
  too_large:
    "This file is too big to load in one go ({kb} kB, the limit is {cap} kB). Split the sheet into two files and load each one separately. For item headers and payment details together, split both sheets at the same row so each pair still matches. For the four-sheet load, split by BL Entry number so each half carries whole entries in all four sheets and the NL amounts in each half still add up to zero.",
  slot_NO_HEADER:
    "The first row of the tab is empty. The first row must hold the column headings.",
  slot_BLANK_HEADER:
    "One of the headings in the first row is blank. Every column needs a heading.",
  slot_NO_ROWS: "The tab has headings but no data rows.",
  slot_DATA_BEYOND_HEADER:
    "Row {row} has data beyond the last heading. Add a heading for that column or clear the cells.",
  slot_EXCEL_ERROR:
    "Row {row}, column {column} contains an Excel error ({text}). Fix it in the spreadsheet.",
  slot_READ_FAILED: "The file could not be read. Is it a .xlsx workbook?",
  controls_amount_invalid:
    "Enter pounds and pence with at most two decimal places, for example 1234.50",
  controls_rows_invalid: "Enter a whole number",
  required_missing: "{slot} is required for this load.",
  commit_repreview:
    "The file was checked again before loading and something had changed or a problem was found. The new results are shown below.",
  commit_retry:
    "The load did not complete and nothing was written. You can try once more. If it fails again, contact support. (A gap may appear in the counter numbers. That is expected and harmless.)",
  commit_retry_failed:
    "The load failed a second time. Nothing was written. Please contact support and quote this message:",
  commit_escalate:
    "The load has stopped and needs support. Nothing was written. Please contact support and quote this message:",
  commit_unknown:
    "The load may have completed but the server could not confirm it. The Batches tab now shows a search for this exact file set. If one batch appears, it loaded. If the list is empty, go back to Load a file, press Check the file and load again.",
  commit_unknown_nobatches:
    "The load may have completed but the server could not confirm it. Ask support to check the batches list for this file set before loading it again.",
  commit_denied:
    "You can check files but not load them. Ask support to add the load permission to your user.",
  server_plain: "The server said: {text}",
};

// Plain-English descriptions for the server's issue codes, shown in the summary
// table. Unknown codes fall back to the server's own message.
export const ISSUES = {
  E_PARSE: "The file could not be read as CSV.",
  E_EMPTY_FILE: "The file is empty.",
  E_INTERIOR_EMPTY_ROW: "There is an empty row in the middle of the data. Delete it.",
  E_ROW_WIDTH: "A row has a different number of cells from the headings.",
  E_UNKNOWN_COLUMN:
    "A heading does not match any column in the ledger table. Check the spelling.",
  E_MISSING_COLUMN: "A column the ledger needs is missing.",
  E_DUPLICATE_COLUMN: "The same heading appears twice.",
  E_COUNTER_PREASSIGNED:
    "The counter column has values in it. The server assigns the counter, so leave the column empty.",
  E_COUNTER_COLUMN_MISSING:
    "The ledger table has no counter column the loader can use. Nothing in the sheet will fix this. Contact support.",
  E_COUNTER_ROWCOUNT_MISMATCH:
    "The two sheets have different numbers of rows. They must match line for line.",
  E_COUNTER_PAIR_ORDER_MISMATCH:
    "The item headers and payment details sheets are not in the same row order, so sort both tabs the same way and export them again.",
  E_ENTRY_EXISTS:
    "Some of the entry numbers in this sheet are already in the ledger, so this block has been used before. For Sales Ledger files this includes numbers already used on the Bought Ledger.",
  E_REF_EXISTS:
    "Some of the invoice references (SL Tran Ref) in this sheet are already in the ledger. Send the file back; do not renumber it here.",
  E_REF_FORMAT:
    "An invoice reference is not IM followed by 6 digits, or the row is not an IM invoice (SL Inv Type IM, SL Tran Type TI). Only IM invoices can be loaded here. Send the file back.",
  E_LINES_KEY_MISSING:
    "The entry number column is missing, or a row has no entry number, so lines cannot be matched to their headers.",
  E_LINES_HEADER_DUPLICATE: "Two header rows have the same SL Entry number.",
  E_LINES_REF_DUPLICATE: "Two header rows have the same invoice reference (SL Tran Ref).",
  E_LINES_LINE_DUPLICATE: "Two lines have the same SLL Entry and SLL Line numbers.",
  E_LINES_ENTRY_UNMATCHED:
    "A line has no header with the same entry number, or a header has no lines.",
  E_LINES_REF_MISMATCH:
    "A line's SLL Inv Key is not the same as its header's SL Tran Ref.",
  E_LINES_ACCOUNT_MISMATCH: "A line's SLL A/C is not the same as its header's SL A/C.",
  E_LINES_TOTAL_MISMATCH:
    "A header's SL Base Total is not the same as the SLL Total of its lines added together.",
  E_NL_ENTRY_UNMATCHED:
    "The NL Entry numbers in this sheet do not match the BL Entry numbers in the item headers sheet.",
  E_NL_ACCOUNT_MISMATCH:
    "The NL A/C column is missing, or a row in it posts to an account other than the one this sheet is for (1200 or 2100).",
  E_NL_PAIR_IDENTICAL:
    "The two Nominal Ledger sheets contain exactly the same rows, so the same tab has probably been chosen twice.",
  E_NL_UNBALANCED:
    "The NL Amount column does not add up to zero across the 1200 and 2100 sheets, so the postings do not balance.",
  E_NUMBER_PREASSIGNED:
    "BL Entry or BL Tran Ref has values in it. The loader assigns both when you load, so clear the column or remove it.",
  E_DUMMY_PAIR_IDENTICAL:
    "Both slots hold the same rows, so the same tab has probably been chosen twice. Pick the second account's tab for the second slot.",
  E_ALLOCATOR_BEHIND:
    "The ledger's last-used counters (Last Entry, Last Journal) sit behind numbers already in the ledger, so the next block would clash. Nothing in the sheet will fix this. Contact support.",
  E_FILE_ALREADY_LOADED:
    "This sheet was already loaded in an earlier batch with a different partner sheet (the detail says which batch, who and when). Loading it again would post every line a second time under new numbers. Check which tabs were picked.",
  E_AMOUNT_FORMAT:
    "An amount is not in a format we can read. Use plain numbers like 1234.50 or -5.00.",
  E_INT_FORMAT: "A whole-number column has something that is not a whole number.",
  E_INT_RANGE: "A number is too large for the column.",
  E_BOOL_FORMAT: "A yes/no column has a value that is not yes, no, true, false, 1 or 0.",
  E_DATE_FORMAT: "A date does not match the date layout you chose.",
  E_DATEFORMAT_UNSUPPORTED: "The date layout is not one the loader accepts.",
  E_NONASCII_UNMAPPABLE:
    "A character cannot be stored (for example an emoji or unusual symbol).",
  E_CONTROL_CHAR: "A cell contains a hidden control character. Retype the cell.",
  E_NULL_IN_NOT_NULL: "A required cell is empty.",
  E_VALUE_TOO_LONG: "A value is longer than the column allows.",
  E_FILE_SET: "The set of files does not match what this load type needs.",
  E_CONTROL_ROWCOUNT_MISMATCH:
    "The number of rows does not match the number you entered.",
  E_CONTROL_TOTAL_MISMATCH: "A column total does not match the figure you entered.",
  E_TABLE_NOT_FOUND: "The ledger table could not be found. Contact support.",
  E_DB: "The database could not be reached or is not set up for the loader. Contact support.",
  E_DUPLICATE_BATCH: "This exact file set has already been loaded.",
  W_NONASCII_SUBSTITUTION:
    "Some accented or special characters were replaced with plain letters.",
  W_STRIPPED_NONEMPTY: "An ignored column had data in it. That data will not be loaded.",
  W_DUMMY_PAIR_UNBALANCED:
    "The amounts across the two sheets do not add up to zero (the column and the difference are in the detail). Dummy payments normally mirror each other; check before loading.",
  W_DUMMY_PAIR_SAME_ACCOUNT:
    "The same BL A/C appears in both sheets. The two sheets are meant to be two different accounts; check this is not the same account's tab from two workbooks.",
  W_ORDERING_UNVERIFIED:
    "The two sheets are matched by row order. Make sure the rows line up.",
  W_NO_COUNTER_UNIQUE_INDEX:
    "The ledger table is missing a safety check. The load cannot go ahead. Contact support.",
  W_ENTRY_BELOW_ALLOCATOR:
    "The numbers in this column sit at or below the ledger's last issued number (Last Entry for BL Entry and SL Entry, Last Payment B/L for BL Tran Ref, Last Manual Inv for SL Tran Ref). That is fine if you moved the last-issued number on by hand before sending. If you did not, check the block is unused.",
  I_TRAILING_EMPTY_ROWS: "Empty rows at the end of the sheet were removed.",
  I_STRIPPED_COLUMN: "A system-managed column was ignored.",
  I_ORDERING_VERIFIED:
    "The item headers and payment details sheets were confirmed to be in the same row order.",
  I_LINES_PAIR_VERIFIED:
    "Every line was matched to its header: same entry number, invoice reference and account, and the line totals add up to the header total.",
  I_ALLOCATOR_READ:
    "The ledger's last used counters were read. For a file that carries its own numbers, compare them with the range in the file. For dummy payments, the detail is the block the loader will take when you load.",
  I_DUMMY_PAIR_BALANCED: "The amounts across the two sheets add up to zero.",
};

// text('flow_cap', { kb: 90 }) -> 'Files up to 90 kB can be loaded in one go.'
export function text(key, params) {
  let s = COPY[key] === undefined ? key : COPY[key];
  if (params) {
    Object.keys(params).forEach((k) => {
      s = s.split("{" + k + "}").join(params[k] === null || params[k] === undefined ? "" : String(params[k]));
    });
  }
  return s;
}

export function flowname(name) {
  return FLOWNAMES[name] || name;
}

export function slotname(name) {
  return SLOTNAMES[name] || name;
}

export function slothint(name) {
  return SLOTHINTS[name] || "";
}

// One line per date from sldatewarnings, e.g.
// [{ date: "15/10/2026", rows: 12 }] -> ["12 rows have an SL Date of 15/10/2026, not today."]
export function sldatelines(groups) {
  return (groups || []).map((g) =>
    g.rows === 1 ? text("sldate_row_one", { date: g.date }) : text("sldate_row", { rows: g.rows, date: g.date })
  );
}
